import { entityService } from '../../../services/entityService';
import { createFileLogger } from '../../../utils/logger';
import '../../../types/electron'; // Import centralized Electron type definitions

const log = createFileLogger('LocalToolExecutor');

interface ExecutionParams {
  script: string;
  inputEntityIds: string[];
  outputName?: string;
  requiresTools?: string[];
  spaceId?: string;
  userId?: string;
}

interface ExecutionResult {
  success: boolean;
  newEntityId?: string;
  newEntityIds?: string[];
  stdout: string;
  stderr: string;
  error?: string;
  exitCode?: number;
}

class LocalToolExecutor {
  /**
   * Check if running in Electron environment.
   */
  private isElectron(): boolean {
    return !!(window as any).riverOS;
  }

  /**
   * Check if specific tools are available on the system.
   */
  async checkToolAvailability(tools: string[]): Promise<{
    available: string[];
    missing: string[];
  }> {
    if (!this.isElectron()) {
      log.warn('[TOOLS] Not running in Electron - local execution unavailable');
      return {
        available: [],
        missing: tools
      };
    }

    const available: string[] = [];
    const missing: string[] = [];
    
    const api = (window as any).riverOS;
    
    for (const tool of tools) {
      try {
        const result = await api.tools.checkTool(tool);
        
        if (result.success && result.available) {
          available.push(tool);
          log.log(`✅ Tool available: ${tool} at ${result.path}`);
        } else {
          missing.push(tool);
          log.log(`❌ Tool not found: ${tool}`);
        }
      } catch (error) {
        missing.push(tool);
        log.log(`❌ Tool check failed: ${tool}`, error);
      }
    }
    
    return { available, missing };
  }

  /**
   * Detect common tools installed on the system.
   */
  async detectCommonTools(): Promise<Record<string, boolean>> {
    if (!this.isElectron()) {
      return {};
    }

    const commonTools = [
      'convert', 'magick', 'ffmpeg', 'ffprobe', 'pandoc', 
      'python3', 'python', 'node', 'gs', 'exiftool',
      'sox', 'cwebp', 'optipng', 'jpegoptim', 'pngquant'
    ];
    
    const capabilities: Record<string, boolean> = {};
    const api = (window as any).riverOS;
    
    for (const tool of commonTools) {
      try {
        const result = await api.tools.checkTool(tool);
        capabilities[tool] = result.success && (result.available ?? false);
      } catch {
        capabilities[tool] = false;
      }
    }
    
    log.log('[TOOL_DETECTION] Available tools:', 
      Object.entries(capabilities)
        .filter(([_, available]) => available)
        .map(([tool]) => tool)
    );
    
    return capabilities;
  }

  /**
   * Request user consent for command execution.
   */
  private async requestUserConsent(script: string, tool?: string): Promise<boolean> {
    return new Promise((resolve) => {
      const timestamp = Date.now();
      
      log.log('[CONSENT] Setting up consent request:', {
        scriptLength: script.length,
        tool,
        timestamp
      });
      
      const timeoutId = setTimeout(() => {
        log.warn('[CONSENT] User consent timeout (60s) - denying execution');
        resolve(false);
      }, 60000); // 1 minute timeout

      const handleResponse = (e: CustomEvent) => {
        log.log('[CONSENT] Received consent response:', {
          approved: e.detail.approved,
          timestamp: e.detail.timestamp,
          matchesRequest: e.detail.timestamp === timestamp
        });
        
        clearTimeout(timeoutId);
        window.removeEventListener('tool-consent-response', handleResponse as EventListener);
        resolve(e.detail.approved);
      };

      window.addEventListener('tool-consent-response', handleResponse as EventListener);
      
      log.log('[CONSENT] Dispatching consent request event...');

      window.dispatchEvent(new CustomEvent('request-tool-consent', {
        detail: {
          script,
          tool,
          timestamp
        }
      }));
      
      log.log('[CONSENT] Consent request event dispatched, waiting for user response...');
    });
  }

  /**
   * Execute tool command locally with user consent.
   */
  async execute(params: ExecutionParams): Promise<ExecutionResult> {
    try {
      // Check if Electron environment
      const hasRiverOS = !!(window as any).riverOS;
      
      log.log('[EXECUTE] Environment check:', {
        hasRiverOS,
        isElectron: this.isElectron(),
        riverOSKeys: hasRiverOS ? Object.keys((window as any).riverOS) : [],
        platform: typeof process !== 'undefined' ? process.platform : 'unknown'
      });
      
      if (!this.isElectron()) {
        log.error('[EXECUTE] ❌ Not in Electron environment');
        return {
          success: false,
          stdout: '',
          stderr: '',
          error: 'Local tool execution requires Electron environment. This feature is only available in the desktop app.',
          exitCode: -1
        };
      }

      // Check required tools if specified
      if (params.requiresTools && params.requiresTools.length > 0) {
        log.log('[EXECUTE] Checking required tools:', params.requiresTools);
        
        const { missing, available } = await this.checkToolAvailability(params.requiresTools);
        
        log.log('[EXECUTE] Tool availability check:', { available, missing });
        
        if (missing.length > 0) {
          log.error('[EXECUTE] ❌ Missing required tools:', missing);
          return {
            success: false,
            stdout: '',
            stderr: `Required tools not found: ${missing.join(', ')}`,
            error: `Missing tools: ${missing.join(', ')}. Please install them to use this feature.`,
            exitCode: -1
          };
        }
        
        log.log('[EXECUTE] ✅ All required tools available');
      }
      
      // Request user consent
      log.log('[CONSENT] Requesting user approval for script execution');
      const approved = await this.requestUserConsent(params.script);
      
      if (!approved) {
        log.log('[CONSENT] User denied execution');
        return {
          success: false,
          stdout: '',
          stderr: '',
          error: 'User denied command execution',
          exitCode: -2
        };
      }
      
      log.log('[CONSENT] User approved execution');
      
      // Get input file paths from entities
      const inputFiles: Array<{ entityId: string; filename: string; localPath: string }> = [];
      
      log.log('[EXECUTE] Fetching input entities:', params.inputEntityIds);
      
      for (const entityId of params.inputEntityIds) {
        log.log('[EXECUTE] Fetching entity:', entityId);
        
        const entity = await entityService.get(entityId);
        
        log.log('[EXECUTE] Entity fetched:', {
          entityId,
          hasEntity: !!entity,
          name: entity?.name,
          hasSkeleton: !!entity?.skeleton,
          skeletonKeys: entity?.skeleton ? Object.keys(entity.skeleton) : []
        });
        
        if (!entity) {
          log.error('[EXECUTE] ❌ Entity not found:', entityId);
          throw new Error(`Entity ${entityId} not found`);
        }
        
        const localPath = entity.skeleton?.localPath || entity.skeleton?.path;
        
        log.log('[EXECUTE] File path check:', {
          entityId,
          hasLocalPath: !!entity.skeleton?.localPath,
          hasPath: !!entity.skeleton?.path,
          localPath,
          isAbsolutePath: localPath?.startsWith('/')
        });
        
        // If path is relative (backend storage path), it needs to be downloaded
        // Pass the download URL to Electron which will handle the download
        if (localPath && !localPath.startsWith('/') && !localPath.match(/^[A-Z]:\\/i)) {
          log.log('[EXECUTE] ⬇️ File stored on backend, will download:', {
            entityId,
            backendPath: localPath
          });
          
          // Get download URL using FILES_ENDPOINTS
          const { FILES_ENDPOINTS } = await import('../../../endpoints');
          const downloadUrl = FILES_ENDPOINTS.read(entity._id || entityId);
          
          log.log('[EXECUTE] Download URL constructed:', {
            entityId,
            url: downloadUrl.substring(0, 100) + '...'
          });
          
          // Store download URL instead of local path
          // The Electron main process will download it to temp location
          inputFiles.push({
            entityId,
            filename: entity.name || `input-${entityId}`,
            localPath: downloadUrl // Electron will detect this as URL and download
          });
          
          log.log('[EXECUTE] ✅ File queued for download:', {
            entityId,
            filename: entity.name
          });
          
          continue; // Skip the regular file path logic below
        }
        
        if (!localPath) {
          log.error('[EXECUTE] ❌ Entity has no file path:', {
            entityId,
            name: entity.name,
            skeleton: entity.skeleton
          });
          throw new Error(`Entity ${entityId} has no local file path`);
        }
        
        inputFiles.push({
          entityId,
          filename: entity.name || `input-${entityId}`,
          localPath
        });
        
        log.log('[EXECUTE] ✅ Input file prepared:', {
          entityId,
          filename: entity.name,
          localPath: localPath.substring(0, 50) + '...'
        });
      }
      
      log.log('[EXECUTE] All input files prepared:', {
        count: inputFiles.length,
        filenames: inputFiles.map(f => f.filename)
      });
      
      log.log('[EXEC] Executing with Electron IPC:', {
        scriptLength: params.script.length,
        inputFiles: inputFiles.map(f => f.filename)
      });
      
      // Get auth token for backend API calls
      const authToken = localStorage.getItem('authToken');
      
      const executeOptions = {
        inputFiles: inputFiles.map(f => ({
          path: f.localPath,
          name: f.filename
        })),
        outputName: params.outputName,
        spaceId: params.spaceId,
        userId: params.userId,
        authToken,
        timeout: 300000 // 5 minutes
      };
      
      console.log('========================================');
      console.log('🚀 ABOUT TO CALL ELECTRON IPC');
      console.log('Command: bash');
      console.log('Script length:', params.script.length);
      console.log('Input files:', executeOptions.inputFiles.length);
      console.log('Has auth token:', !!authToken);
      console.log('========================================');
      
      log.log('[EXECUTE] Calling riverOS.tools.execute:', {
        command: 'bash',
        args: ['-c', params.script.substring(0, 100) + '...'],
        inputFileCount: executeOptions.inputFiles.length,
        hasAuthToken: !!authToken,
        spaceId: params.spaceId
      });
      
      // Execute via Electron IPC with bash script
      // The main process will handle creating temp directories and file management
      const api = (window as any).riverOS;
      
      if (!api || !api.tools || !api.tools.execute) {
        log.error('[EXECUTE] ❌ riverOS.tools.execute not available:', {
          hasRiverOS: !!api,
          hasTools: !!api?.tools,
          toolsKeys: api?.tools ? Object.keys(api.tools) : []
        });
        throw new Error('riverOS.tools.execute API not available');
      }
      
      const result = await api.tools.execute('bash', ['-c', params.script], executeOptions);
      
      log.log('[EXECUTE] riverOS.tools.execute returned:', {
        success: result.success,
        hasResult: !!result.result,
        resultKeys: result.result ? Object.keys(result.result) : [],
        error: result.error,
        newEntityIds: result.result?.newEntityIds,
        newEntityIdsLength: result.result?.newEntityIds?.length
      });
      
      if (!result.success || !result.result) {
        log.error('[EXECUTE] ❌ Execution failed:', {
          success: result.success,
          error: result.error,
          result: result.result
        });
        
        return {
          success: false,
          stdout: result.result?.stdout || '',
          stderr: result.result?.stderr || result.error || '',
          error: result.error || 'Command execution failed',
          exitCode: result.result?.code || -1
        };
      }
      
      log.log('[EXEC] Command completed:', {
        exitCode: result.result.code,
        stdoutLength: result.result.stdout.length,
        stderrLength: result.result.stderr.length,
        hasNewEntityIds: !!result.result.newEntityIds,
        newEntityIdsCount: result.result.newEntityIds?.length || 0,
        stdout: result.result.stdout,
        stderr: result.result.stderr
      });
      
      // The main process will return entity IDs of created output files
      const response = result.result as any;
      
      log.log('[EXECUTE] Checking for output entities:', {
        hasNewEntityIds: !!response.newEntityIds,
        newEntityIds: response.newEntityIds,
        newEntityIdsType: typeof response.newEntityIds,
        newEntityIdsLength: response.newEntityIds?.length,
        responseKeys: Object.keys(response)
      });
      
      if (!response.newEntityIds || response.newEntityIds.length === 0) {
        log.warn('[EXECUTE] ⚠️ No output entities created:', {
          exitCode: result.result.code,
          hasStdout: !!result.result.stdout,
          hasStderr: !!result.result.stderr,
          stdout: result.result.stdout,
          stderr: result.result.stderr
        });
        
        return {
          success: false,
          stdout: result.result.stdout,
          stderr: result.result.stderr,
          error: 'No output files generated',
          exitCode: result.result.code
        };
      }
      
      log.log('[EXECUTE] ✅ Output entities created:', {
        newEntityIds: response.newEntityIds,
        count: response.newEntityIds.length
      });
      
      return {
        success: true,
        newEntityId: response.newEntityIds[0],
        newEntityIds: response.newEntityIds,
        stdout: result.result.stdout,
        stderr: result.result.stderr,
        exitCode: result.result.code
      };
      
    } catch (error: any) {
      log.error('[EXEC] Execution failed:', error);
      
      return {
        success: false,
        stdout: '',
        stderr: error.message || '',
        error: error.message || 'Execution failed',
        exitCode: -1
      };
    }
  }
}

export const localToolExecutor = new LocalToolExecutor();
